import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useMemo, useState } from 'react';
import { Calendar, CircleDot, FolderOpen, Hash, ListPlus, Paperclip, ToggleLeft, Type, } from 'lucide-react';
import { Button } from '../primitives/Button.js';
import { defaultPalette } from './palette-entries.js';
/** Map a FHIR item.type to a field glyph (no emoji, per the design system). */
function fieldTypeIcon(type) {
    switch (type) {
        case 'choice':
            return _jsx(CircleDot, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" });
        case 'attachment':
            return _jsx(Paperclip, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" });
        case 'decimal':
        case 'integer':
            return _jsx(Hash, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" });
        case 'date':
            return _jsx(Calendar, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" });
        case 'boolean':
            return _jsx(ToggleLeft, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" });
        default:
            return _jsx(Type, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" });
    }
}
function normaliseLabel(label) {
    if (label === undefined)
        return undefined;
    const trimmed = label.trim().toLowerCase().replace(/\s+/g, ' ');
    return trimmed.length === 0 ? undefined : trimmed;
}
function collectAllItems(items) {
    const out = [];
    const traverse = (list) => {
        for (const it of list) {
            out.push(it);
            if (it.type === 'group' && it.item) {
                traverse(it.item);
            }
        }
    };
    traverse(items);
    return out;
}
export function lintCanvasItems(items) {
    const findings = [];
    const allItems = collectAllItems(items);
    const byLinkId = new Map();
    const byLabel = new Map();
    for (const item of allItems) {
        if (item.type === 'display')
            continue;
        byLinkId.set(item.linkId, (byLinkId.get(item.linkId) ?? 0) + 1);
        const label = normaliseLabel(item.text);
        if (label !== undefined) {
            const occurrences = byLabel.get(label) ?? [];
            occurrences.push(item.linkId);
            byLabel.set(label, occurrences);
        }
    }
    for (const item of allItems) {
        if (item.type === 'display')
            continue;
        const dupId = (byLinkId.get(item.linkId) ?? 0) > 1;
        if (dupId) {
            findings.push({
                code: 'LINT_DUPLICATE_LINK_ID',
                severity: 'error',
                linkId: item.linkId,
                message: `Duplicate linkId — every item must have a unique linkId.`,
            });
        }
        const label = normaliseLabel(item.text);
        if (label !== undefined) {
            const occurrences = byLabel.get(label) ?? [];
            if (occurrences.length > 1) {
                findings.push({
                    code: 'LINT_DUPLICATE_LABEL',
                    severity: 'warning',
                    linkId: item.linkId,
                    message: `Duplicate label — ${String(occurrences.length)} items share this text.`,
                });
            }
        }
    }
    return findings;
}
export function Canvas({ items, selectedLinkId, collapsedSections = {}, onSelect, onMove, onRemove, onToggleCollapse, onMoveRelative, onAddRelative, onAddInsideSection, lintFindings, }) {
    const [dragOverState, setDragOverState] = useState({
        targetId: null,
        position: null,
    });
    const [draggingId, setDraggingId] = useState(null);
    const findingsByLinkId = useMemo(() => {
        const source = lintFindings ?? lintCanvasItems(items);
        const out = new Map();
        for (const f of source) {
            const list = out.get(f.linkId) ?? [];
            list.push(f);
            out.set(f.linkId, list);
        }
        return out;
    }, [items, lintFindings]);
    const handleDragStart = (e, linkId) => {
        setDraggingId(linkId);
        e.dataTransfer.setData('text/plain', `canvas:${linkId}`);
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragEnd = () => {
        setDraggingId(null);
        setDragOverState({ targetId: null, position: null });
    };
    const handleDragOver = (e, targetId, isGroup) => {
        e.preventDefault();
        e.stopPropagation();
        // Prevent dragging an item onto itself
        if (draggingId === targetId)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const relY = e.clientY - rect.top;
        let pos = 'before';
        if (isGroup) {
            const threshold = rect.height * 0.25;
            if (relY < threshold) {
                pos = 'before';
            }
            else if (relY > rect.height - threshold) {
                pos = 'after';
            }
            else {
                pos = 'inside';
            }
        }
        else {
            pos = relY > rect.height / 2 ? 'after' : 'before';
        }
        setDragOverState({ targetId, position: pos });
    };
    const handleDragLeave = () => {
        setDragOverState({ targetId: null, position: null });
    };
    const handleDrop = (e, targetId, isGroup) => {
        e.preventDefault();
        e.stopPropagation();
        const data = e.dataTransfer.getData('text/plain');
        if (!data)
            return;
        const position = dragOverState.position || (isGroup ? 'inside' : 'after');
        setDragOverState({ targetId: null, position: null });
        if (data.startsWith('palette:')) {
            const fieldId = data.replace('palette:', '');
            const entry = defaultPalette.find((p) => p.id === fieldId);
            if (entry) {
                onAddRelative(entry, targetId, position);
            }
        }
        else if (data.startsWith('canvas:')) {
            const activeId = data.replace('canvas:', '');
            if (activeId !== targetId) {
                onMoveRelative(activeId, targetId, position);
            }
        }
    };
    const renderItemRow = (item, isChild) => {
        const selected = item.linkId === selectedLinkId;
        const isGroup = item.type === 'group';
        const isCollapsed = isGroup && collapsedSections[item.linkId];
        const itemFindings = findingsByLinkId.get(item.linkId) ?? [];
        const topSeverity = itemFindings.find((f) => f.severity === 'error')
            ? 'error'
            : itemFindings.find((f) => f.severity === 'warning')
                ? 'warning'
                : undefined;
        const showDragIndicator = dragOverState.targetId === item.linkId;
        const dragPos = dragOverState.position;
        return (_jsxs("div", { className: `canvas-item-wrapper 
          ${selected ? 'selected' : ''} 
          ${isGroup ? 'section-group-card' : 'field-row-card'} 
          ${isChild ? 'child-field' : ''}
          ${draggingId === item.linkId ? 'dragging-active' : ''}
          ${showDragIndicator && dragPos === 'before' ? 'drag-indicator-before' : ''}
          ${showDragIndicator && dragPos === 'after' ? 'drag-indicator-after' : ''}
          ${showDragIndicator && dragPos === 'inside' ? 'drag-indicator-inside' : ''}
        `, "data-link-id": item.linkId, "data-lint-severity": topSeverity ?? 'none', draggable: true, onDragStart: (e) => handleDragStart(e, item.linkId), onDragEnd: handleDragEnd, onDragOver: (e) => handleDragOver(e, item.linkId, isGroup), onDragLeave: handleDragLeave, onDrop: (e) => handleDrop(e, item.linkId, isGroup), onClick: (e) => {
                e.stopPropagation();
                onSelect(item.linkId);
            }, children: [isGroup ? (
                /* Section / Group Card Layout */
                _jsxs("div", { className: "section-card-header", children: [_jsxs("div", { className: "section-card-header-left", children: [_jsx("button", { type: "button", className: "section-collapse-toggle", onClick: (e) => {
                                        e.stopPropagation();
                                        onToggleCollapse(item.linkId);
                                    }, children: isCollapsed ? '▶' : '▼' }), _jsx("span", { className: "section-card-icon", children: _jsx(FolderOpen, { size: 16, strokeWidth: 1.6, "aria-hidden": "true" }) }), _jsx("span", { className: "section-card-title", children: item.text || 'Untitled Section' }), _jsxs("span", { className: "section-card-field-count", children: [(item.item ?? []).length, " fields"] })] }), _jsxs("div", { className: "section-card-header-actions", onClick: (e) => e.stopPropagation(), children: [_jsx(Button, { variant: "ghost", "aria-label": "Move up", onClick: () => onMove(item.linkId, -1), children: '↑' }), _jsx(Button, { variant: "ghost", "aria-label": "Move down", onClick: () => onMove(item.linkId, 1), children: '↓' }), _jsx(Button, { variant: "danger", "aria-label": "Remove", onClick: () => onRemove(item.linkId), children: '×' })] })] })) : (
                /* Individual Field Layout */
                _jsxs("div", { className: "field-row-content", children: [_jsx("div", { className: "field-row-drag-handle", children: "\u22EE\u22EE" }), _jsx("span", { className: "field-row-icon", children: fieldTypeIcon(item.type) }), _jsxs("div", { className: "field-row-info", children: [_jsx("span", { className: "field-row-label", children: item.text || 'Untitled Field' }), _jsx("span", { className: "field-row-type badge", children: item.type }), item.required && _jsx("span", { className: "field-row-required-badge", children: "required" })] }), itemFindings.map((f) => (_jsx("span", { className: `badge badge-${f.severity} canvas-lint-chip`, title: f.message, children: f.code === 'LINT_DUPLICATE_LINK_ID' ? 'dup ID' : 'dup label' }, f.code))), _jsxs("div", { className: "field-row-actions", onClick: (e) => e.stopPropagation(), children: [_jsx(Button, { variant: "ghost", "aria-label": "Move up", onClick: () => onMove(item.linkId, -1), children: '↑' }), _jsx(Button, { variant: "ghost", "aria-label": "Move down", onClick: () => onMove(item.linkId, 1), children: '↓' }), _jsx(Button, { variant: "danger", "aria-label": "Remove", onClick: () => onRemove(item.linkId), children: '×' })] })] })), isGroup && !isCollapsed && (_jsxs("div", { className: "section-card-children", children: [(item.item ?? []).length === 0 ? (_jsx("div", { className: "section-card-empty-placeholder", children: "Drag fields or drop them here." })) : (_jsx("div", { className: "section-children-list", children: (item.item ?? []).map((child) => renderItemRow(child, true)) })), _jsx("div", { className: "section-card-footer", onClick: (e) => e.stopPropagation(), children: defaultPalette[0] !== undefined && (_jsx("button", { type: "button", className: "section-add-field-btn", onClick: () => {
                                    const first = defaultPalette[0];
                                    if (first !== undefined)
                                        onAddInsideSection(item.linkId, first);
                                }, children: "+ Add text field" })) })] }))] }, item.linkId));
    };
    if (items.length === 0) {
        return (_jsxs("div", { className: `canvas-empty-state ${dragOverState.targetId === 'canvas-root' ? 'drag-active' : ''}`, onDragOver: (e) => {
                e.preventDefault();
                setDragOverState({ targetId: 'canvas-root', position: 'inside' });
            }, onDragLeave: () => setDragOverState({ targetId: null, position: null }), onDrop: (e) => {
                e.preventDefault();
                const data = e.dataTransfer.getData('text/plain');
                setDragOverState({ targetId: null, position: null });
                if (data && data.startsWith('palette:')) {
                    const fieldId = data.replace('palette:', '');
                    const entry = defaultPalette.find((p) => p.id === fieldId);
                    if (entry)
                        onAddRelative(entry, 'canvas-root', 'inside');
                }
            }, children: [_jsx("span", { className: "canvas-empty-icon", children: _jsx(ListPlus, { size: 28, strokeWidth: 1.4, "aria-hidden": "true" }) }), _jsx("h3", { children: "Your form is empty" }), _jsx("p", { children: "Drag individual fields here, or click a section template to load a predefined block." })] }));
    }
    return (_jsx("div", { className: "canvas-container-scroll", onClick: () => onSelect(undefined), children: _jsx("div", { className: "canvas-stack-layout", children: items.map((item) => renderItemRow(item, false)) }) }));
}
//# sourceMappingURL=Canvas.js.map